import React from 'react';

/**
 * Wejście sekcji przy przewijaniu, bez ryzyka dla treści.
 *
 * Elementy są domyślnie widoczne (prerender, brak JS, `prefers-reduced-motion`).
 * Dopiero po zamontowaniu hook chowa te oznaczone `data-reveal` /
 * `data-reveal-group`, które leżą POD pierwszym ekranem, i odsłania je, gdy
 * wjadą w kadr. To, co widać od razu po załadowaniu, nigdy nie mruga.
 *
 * - `data-reveal` — element wjeżdża sam.
 * - `data-reveal-group` — dzieci wjeżdżają kaskadą (`--i` = kolejność, do 7),
 *   a grupa dostaje wartość `in`, na której można zawiesić własną animację
 *   (np. linię postępu w `.bp-flow`).
 */
export function useScrollReveal(): void {
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Prerender zapisuje HTML w chwili zrzutu — schowane elementy zostałyby w nim schowane.
    if (navigator.webdriver) return;

    const fold = window.innerHeight * 0.92;
    const singles = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    const groups = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal-group]'));
    const tracked: HTMLElement[] = [];

    for (const el of singles) {
      if (el.getBoundingClientRect().top < fold) continue;
      el.setAttribute('data-reveal', 'pending');
      tracked.push(el);
    }
    for (const group of groups) {
      if (group.getBoundingClientRect().top < fold) continue;
      group.setAttribute('data-reveal-group', 'pending');
      Array.from(group.children).forEach((child, i) => {
        (child as HTMLElement).style.setProperty('--i', String(Math.min(i, 7)));
      });
      tracked.push(group);
    }
    if (tracked.length === 0) return;

    const revealAll = () => {
      for (const el of tracked) {
        if (el.getAttribute('data-reveal') === 'pending') el.setAttribute('data-reveal', 'in');
        if (el.getAttribute('data-reveal-group') === 'pending') el.setAttribute('data-reveal-group', 'in');
      }
    };

    // Bezpiecznik: obserwator zawsze zgłasza się zaraz po `observe()`. Jeśli
    // tego nie zrobi (karta otwarta w tle, nietypowa przeglądarka), treść nie
    // może zostać schowana — odsłaniamy wszystko bez animacji.
    let alive = false;
    const failsafe = window.setTimeout(() => {
      if (alive) return;
      io.disconnect();
      revealAll();
    }, 2500);

    const io = new IntersectionObserver(
      (entries) => {
        alive = true;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          if (el.hasAttribute('data-reveal-group')) el.setAttribute('data-reveal-group', 'in');
          else el.setAttribute('data-reveal', 'in');
          io.unobserve(el);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 },
    );
    tracked.forEach((el) => io.observe(el));

    return () => {
      window.clearTimeout(failsafe);
      io.disconnect();
      // Po odmontowaniu nic nie może zostać schowane.
      revealAll();
    };
  }, []);
}
